import {
    SchedulePolicyKind,
    getSchedulePolicyKindsForType,
} from "@common/helpers/domains/accounting/schedulePolicyKind";
import { ScheduleType } from "@common/types/domains/accounting/schedule";
import {
    EXPENSE_SUB_MODE_FILTER,
    REVENUE_SUB_MODE_FILTER,
    ScheduleSubModeFilter,
} from "./scheduleSubModeFilters";

/** One schedules hub page: the data that tells the shared hub components which schedules it owns. */
export interface ScheduleHubScope {
    path: string;
    /** Sidebar item and breadcrumb. */
    label: string;
    scheduleType: ScheduleType;
    policyKinds: SchedulePolicyKind[];
    defaultKind: SchedulePolicyKind;
    isGatedByExpensesFlag: boolean;
    pickerSectionTitle: string;
    schedulesTabLabel: string;
    policiesEmptyStateBody: string;
    schedulesEmptyStateHeader: string;
    subModeFilter: ScheduleSubModeFilter;
}

export const REVENUE_SCHEDULE_HUB_SCOPE: ScheduleHubScope = {
    path: "/revenue",
    label: "Revenue recognition",
    scheduleType: ScheduleType.revenue,
    policyKinds: getSchedulePolicyKindsForType(ScheduleType.revenue),
    defaultKind: SchedulePolicyKind.deferredRevenue,
    isGatedByExpensesFlag: false,
    pickerSectionTitle: "Revenue",
    schedulesTabLabel: "Recognition schedules",
    policiesEmptyStateBody: "Create a policy to recognize revenue over time.",
    schedulesEmptyStateHeader: "No recognition schedules found",
    subModeFilter: REVENUE_SUB_MODE_FILTER,
};

export const EXPENSE_SCHEDULE_HUB_SCOPE: ScheduleHubScope = {
    path: "/expenses",
    label: "Expenses",
    scheduleType: ScheduleType.expense,
    policyKinds: getSchedulePolicyKindsForType(ScheduleType.expense),
    defaultKind: SchedulePolicyKind.prepaidExpense,
    isGatedByExpensesFlag: true,
    pickerSectionTitle: "Expenses",
    schedulesTabLabel: "Expense schedules",
    policiesEmptyStateBody: "Create a policy to spread expenses over time.",
    schedulesEmptyStateHeader: "No expense schedules found",
    subModeFilter: EXPENSE_SUB_MODE_FILTER,
};

export const SCHEDULE_HUB_SCOPES: ScheduleHubScope[] = [
    REVENUE_SCHEDULE_HUB_SCOPE,
    EXPENSE_SCHEDULE_HUB_SCOPE,
];

export function getAvailableScheduleHubScopes(options: {
    isExpensesAvailable: boolean;
}): ScheduleHubScope[] {
    return SCHEDULE_HUB_SCOPES.filter(
        (scope) => options.isExpensesAvailable || !scope.isGatedByExpensesFlag,
    );
}

/**
 * The hubs a surface works with: its own inside a hub page, every available
 * one on unscoped surfaces (bill, invoice and transaction drawers).
 */
export function resolveScheduleHubScopes(options: {
    scope: ScheduleHubScope | null;
    isExpensesAvailable: boolean;
}): ScheduleHubScope[] {
    return options.scope
        ? [options.scope]
        : getAvailableScheduleHubScopes(options);
}

export function matchesScheduleHubScope(
    item: { type: ScheduleType },
    scope: ScheduleHubScope | null,
): boolean {
    return scope === null || item.type === scope.scheduleType;
}

/** The hub that lists a schedule type; types without their own hub list under revenue. */
export function findScheduleHubScope(
    scheduleType: ScheduleType,
): ScheduleHubScope {
    return (
        SCHEDULE_HUB_SCOPES.find(
            (scope) => scope.scheduleType === scheduleType,
        ) ?? REVENUE_SCHEDULE_HUB_SCOPE
    );
}

/** Where a link scoped to a policy of another hub's type should go instead; null to stay. */
export function resolveScheduleHubRedirectPath(options: {
    scope: ScheduleHubScope;
    templateId: string | null;
    templates: Array<{ id: string; type: ScheduleType }>;
    availableScopes: ScheduleHubScope[];
}): string | null {
    const { scope, templateId, templates, availableScopes } = options;
    const template = templateId
        ? templates.find((candidate) => candidate.id === templateId)
        : undefined;
    if (!template || matchesScheduleHubScope(template, scope)) {
        return null;
    }
    const target = findScheduleHubScope(template.type);
    const isTargetAvailable = availableScopes.some(
        (available) => available.path === target.path,
    );
    return isTargetAvailable && target.path !== scope.path ? target.path : null;
}
